
import { FSXABaseAppLayout, FSXAGetters } from 'fsxa-pattern-library'
import {
  Component, ProvideReactive, Watch,
} from 'nuxt-property-decorator'
import { CommitField } from 'gitlog'
import { ProjectProperties } from 'fsxa-api'
import { ApplicationInsights } from '@microsoft/applicationinsights-web'
import IBreadcrumbItem from '../../shared/general/interfaces/IBreadcrumbItem'
import { trackPI, paramTracking } from '../../shared/general/tracking/TrackingService'
import StoreSingleton from '../../shared/general/services/StoreSingleton'
import { langToFsxaLocale } from '../../shared/general/services/StoreService'
import { fetchFilteredPrivacyKeys } from '../../shared/general/services/privacy/PrivacySettingsService'
import { Logger } from '../../shared/general/logger/Logger'
import { nuxt } from '../../shared/general/logger/LogKey'
import fetchMaintenanceData from '../../shared/fsxa/services/MaintenanceService'
import IOption from '../../shared/general/interfaces/IOption'
import { IIdMap } from '../../shared/fsxa/interfaces/IIdMap'

@Component({ name: 'AppLayout' })
export default class AppLayout extends FSXABaseAppLayout {
  @ProvideReactive('triggerRouteChange') triggerRouteChange_ : Function = this.triggerRouteChange

  @ProvideReactive('getUrlByPageId') getUrlByPageId_ : Function = this.getUrlByPageId

  @ProvideReactive('globalSettings') globalSettings_ : ProjectProperties | null = null

  private appInsights : ApplicationInsights | undefined = undefined

  private piTracked : boolean = false

  async created () {
    StoreSingleton.init(this.$store)
    this.globalSettings_ = this.globalSettings
    this.$store.commit('Locales/set', this.languages)

    if (process.client) {
      window.addEventListener('titleUpdated', (event : Event) => {
        if (this.piTracked) return
        this.piTracked = true
        trackPI((event as CustomEvent).detail.title)
      })
    }

    const locale = this.localeFromURL
    if (locale && locale.toUpperCase() !== this.locale.toUpperCase()) {
      await this.languageChange(locale)
    }

    const { pt_homepage: homepage, pt_contact_page: contactPage } = this.globalSettings?.data.ps_header ?? {}
    if (homepage) this.$store.commit('PageHeader/setHomepage', this.getUrlByPageId(homepage.referenceId))
    if (contactPage) this.$store.commit('PageHeader/setContactPage', this.getUrlByPageId(contactPage.referenceId))

    this.storeSiteIdentifier()
    this.storePath()
    this.setInitialPrivacySettings()
    this.logBuildIndicator()
    this.setParamTracking()
  }

  async mounted () {
    await this.processMaintenanceData()
    this.$store.commit('Viewport/set', window.innerWidth)

    window.addEventListener('resize', () => {
      this.$store.commit('Viewport/set', window.innerWidth)
    })

    this.getCampaignParameter()

    // browser back/forward button language change
    window.onpopstate = () => {
      // 1 is the language part in our URLs (/de/whatever-else -> ['' , 'de', 'whatever-else'])
      const lang = window.location.pathname.split('/')[1]
      const localeFromURL = langToFsxaLocale(lang)

      if (!localeFromURL || localeFromURL === this.locale) return

      this.$store.commit('Locale/set', localeFromURL)
      this.languageChange(localeFromURL)
    }
  }

  private async setInitialPrivacySettings () : Promise<void> {
    if (!process.client) return

    const ls = JSON.parse(localStorage.getItem('privacySettings') || '{}')
    if (!Object.keys(ls).length) {
      const keysToSet = await fetchFilteredPrivacyKeys(
        this.globalSettings?.data?.ps_country_project_settings?.pt_default_cookies || [],
        this.locale,
      )
      const vendors = keysToSet.map((key) => ({
        key,
        value: true,
      }))

      this.$store.commit('PrivacySettings/setStateAll', vendors)
      return
    }

    const vendors = Object.entries(ls).map(([key, value]) => ({
      key,
      value: value === 'true',
    }))
    this.$store.commit('PrivacySettings/setStateAll', vendors)
  }

  private async processMaintenanceData () : Promise<void> {
    if (this.$store.state.Page.maintenanceBarVisible) return
    const { activate, text } = await fetchMaintenanceData(this.$store.state.Locale.fsxaLocale)
    if (!activate || !text.length) return

    this.$store.commit('Page/setMaintenanceBar', {
      visible: sessionStorage.getItem('show-maintenance-bar') !== 'false',
      text,
    })
  }

  private async getCampaignParameter () : Promise<boolean> {
    try {
      const urlParams : URLSearchParams = new URLSearchParams(window.location.search)
      const locationId = urlParams.get('locationId') || ''
      urlParams.delete('locationId')
      await this.$store.dispatch('Locations/setCampaignLocationId', {
        locationId,
        locale: this.locale,
      })

      const pathname = urlParams.toString()
        ? `${window.location.pathname}?${urlParams.toString()}`
        : window.location.pathname

      window.history.replaceState({}, document.title, pathname + window.location.hash)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(e)
    }

    return true
  }

  async serverPrefetch () {
    this.$store.commit('PageRefMapping/set', this.$config.FSXA_REMOTE_DATASET_PAGEREF_MAPPING)
    this.$store.commit('Locales/set', this.languages)
    this.$store.commit('Locale/set', this.localeFromURL)
    await this.$store.dispatch('GlobalLabels/fetchForLanguage', this.localeFromURL || process.env.FSXA_LOCALE || 'en_US')
  }

  private async languageChange (locale : string) : Promise<void> {
    this.$store.commit('Locale/set', locale)
    await this.$store.dispatch('GlobalLabels/fetchForLanguage', this.$store.state.Locale.fsxaLocale)

    if (process.client) document.body.classList.remove('cb-visible')

    try {
      await this.triggerRouteChange({ locale: this.$store.state.Locale.fsxaLocale })
    } finally {
      const { pt_homepage: homepage, pt_contact_page: contactPage } = this.globalSettings?.data.ps_header ?? {}
      if (homepage) this.$store.commit('PageHeader/setHomepage', this.getUrlByPageId(homepage.referenceId))
      if (contactPage) this.$store.commit('PageHeader/setContactPage', this.getUrlByPageId(contactPage.referenceId))

      await this.$nextTick()

      this.$store.commit('PrivacySettings/reset')
      await this.setInitialPrivacySettings()
      // trigger cookie banner resize
      setTimeout(() => {
        if (process.client) window.dispatchEvent(new CustomEvent('resize'))
      }, 500)
    }
  }

  head () {
    return {
      htmlAttrs: {
        lang: this.localeFromURL.split('_')?.[0] || 'en',
      },
    }
  }

  private get languages () : IOption[] {
    const currentLocaleFirst = (option : IOption) => (
      option.value.toString().toLowerCase() === this.locale.toLowerCase() ? -1 : 1
    )
    const languages : { key : string, value : string }[] = this.globalSettings?.data?.ps_language || []
    return languages.map(({ key, value }) => {
      const upperCaseKey = key.toUpperCase()
      const localeReplacement = this.$config?.FSXA_LOCALE_REPLACEMENT

      return {
        value: localeReplacement?.[upperCaseKey]
          ? `${localeReplacement[upperCaseKey].language}_${localeReplacement[upperCaseKey].country}`
          : `${key}`,
        label: value,
      }
    }).sort(currentLocaleFirst)
  }

  private get homepageUrl () : string {
    return this.$store.state.PageHeader.homepage
  }

  private get breadcrumbItems () : IBreadcrumbItem[] {
    if (!this.currentPage) return []

    const currentPageElement = this.idMap[this.currentPage.caasDocumentId]
    const { parentIds } = currentPageElement || {}
    if (!parentIds) return []

    const isLastElement = (index : number) => index === parentIds.length - 1
    const breadcrumbs : IBreadcrumbItem[] = [
      ...[{
        key: '0',
        globalLabel: 'homepage_label',
        link: this.homepageUrl,
      }],
      ...parentIds.map((parentId, index) => ({
        key: this.idMap[parentId].caasDocumentId,
        label: this.idMap[parentId].label,
        link: isLastElement(index) ? undefined : this.getUrlByPageId(parentId) || '#',
      }))]
    this.$store.commit('Breadcrumbs/set', breadcrumbs)

    return breadcrumbs
  }

  private get idMap () : IIdMap {
    return this.$store.getters[FSXAGetters.navigationData]?.idMap || {}
  }

  private storePath () : void {
    const path = this.breadcrumbItems.map((item) => item.key)
    this.$store.commit('Navigation/set', path)
  }

  private logBuildIndicator () : void {
    if (this.$config.FSXA_TENANT_ID === 'dekra-prod') return

    const traceLog = (msg : string) => {
      // eslint-disable-next-line no-console
      console.info(msg)
      this.appInsights?.trackTrace({ message: msg })
    }

    // eslint-disable-next-line no-console
    console.group('BUILD INDICATOR')
    traceLog(`RUN_TIMESTAMP: ${new Date(this.$config.RUN_TIMESTAMP).toISOString()}`)
    traceLog(`BUILD_TIMESTAMP: ${new Date(this.$config.BUILD_TIMESTAMP).toISOString()}`)
    traceLog(`BUILD_NUMBER: ${this.$config.BUILD_NUMBER}`)
    traceLog(`PACKAGE_LOCK_HASH: ${this.$config.PACKAGE_LOCK_HASH}`)
    // eslint-disable-next-line no-console
    console.groupCollapsed('FSXA')
    traceLog(`FSXA_API_VERSION: ${this.$config.FSXA_API_VERSION}`)
    traceLog(`FSXA_NUXT_MODULE_VERSION: ${this.$config.FSXA_NUXT_MODULE_VERSION}`)
    traceLog(`FSXA_PATTERN_LIBRARY_VERSION: ${this.$config.FSXA_PATTERN_LIBRARY_VERSION}`)
    // eslint-disable-next-line no-console
    console.groupEnd()
    // eslint-disable-next-line no-console
    console.groupCollapsed('CHANGELOG')
    const gitCommits : {
      [Property in CommitField] : string;
    }[] = this.$config.GIT_COMMITS
    gitCommits.forEach((c) => {
      const {
        hash, committerDate, subject, body,
      } = c
      // eslint-disable-next-line no-console
      console.info(`${hash}\n${new Date(committerDate).toLocaleString()}\n${subject}\n${body}`)
    })
    // eslint-disable-next-line no-console
    console.groupEnd()
    // eslint-disable-next-line no-console
    console.groupEnd()
  }

  private get localeFromURL () : string {
    // 1 is the language part in our URLs (/de/whatever-else -> ['' , 'de', 'whatever-else'])
    const lang = this.$router.currentRoute.fullPath.split('/')[1]
    const locale = langToFsxaLocale(lang)
    return locale || this.locale
  }

  private resetCampaignParameter () : void {
    this.$store.commit('Locations/resetCampaignLocationId')
  }

  private setParamTracking () : void {
    if (!process.client) return

    const params = paramTracking()
    if (Object.keys(params).length) {
      this.$store.commit('Tracking/set', params)
      return
    }
    try {
      const sessionParams = JSON.parse(sessionStorage.getItem('trackingParams') || '{}')
      if (Object.keys(sessionParams).length) {
        this.$store.commit('Tracking/set', sessionParams)
      }
    } catch (e) {
      Logger.warn(nuxt, 'Could not parse tracking params from session storage', e)
    }
  }

  private storeSiteIdentifier () : void {
    const identifier = this.globalSettings?.data.ps_header?.pt_site_identifier_text || ''
    this.$store.commit('PageHeader/setSiteIdentifier', identifier)
  }

  private initAppInsights () : void {
    this.appInsights = new ApplicationInsights({
      config: {
        connectionString: this.$config?.APPLICATIONINSIGHTS_CONNECTION_STRING,
      },
    })
    try {
      this.appInsights.loadAppInsights()
      this.appInsights.trackPageView()
    } catch (e) {
      Logger.warn(nuxt, 'Could not initialize App Insights | initAppInsights |', e)
    }
  }

  @Watch('$route', { immediate: true, deep: true })
  private routeChange () : void {
    this.resetCampaignParameter()
  }

  @Watch('$store.state.PrivacySettings.settings.appInsights')
  private appInsightsChange (appInsightsEnabled : boolean) : void {
    if (!appInsightsEnabled || !!this.appInsights) return

    this.initAppInsights()
  }
}
